import { debug, warn } from "./core/config.js";
import { HttpError, ServerDataSource, type DataSource } from "./core/datasource.js";
import { attrSelector } from "./core/dom.js";
import { runRender, runTransformItems } from "./core/hooks.js";
import {
  buildTemplatePicker,
  findTemplate,
  renderItems,
  type TemplatePicker,
} from "./core/render.js";
import type { HookCtx, Item, ListConfig, ListState } from "./core/types.js";
import { applyFacets, bindFilterControls } from "./filtering/filtering.js";
import { SearchDataSource } from "./global-search/datasource.js";
import { bindGlobalSearchInput, initGlobalSearch, showIdle } from "./global-search/search-block.js";
import { findSearchTemplates } from "./global-search/templates.js";
import { bindLoadMore } from "./load-more/load-more.js";
import { bindListSearchInput } from "./search/search.js";

/**
 * One `data-slater-list` (or `data-slater-search-list`) on the page. Owns its
 * state, its DOM and its request lifecycle; a failure in one never touches
 * another. Search mode is the same machine pointed at `GET /:siteId/search`:
 * no filters/sort/facets, an idle state while the query is empty, and a
 * per-collection template picker instead of a single cloned card.
 *
 * This class is the shared lifecycle only — each feature's logic lives in its
 * own folder (load-more/, filtering/, search/, global-search/) as functions
 * over this instance, wired in `bindControls`/`init`. See src/README.md.
 */
export class SlaterList {
  /** seam: read and mutated by the feature binders (load-more/, filtering/, search/, global-search/). */
  readonly state: ListState;
  /** seam: global-search/ aborts the in-flight request when going idle. */
  readonly source: DataSource;
  /** seam: populated and read by filtering/ (also cleared by core clearAll). */
  readonly filterControls = new Map<string, Element[]>();

  private template: Element | TemplatePicker | null = null;
  /** Webflow's server-rendered items, kept until we have real data to swap in. */
  private ssrIntact = true;
  /**
   * Every item currently on screen, across pages. Replayed on a bfcache
   * restore. seam: reset by global-search/ (idle).
   */
  rendered: Item[] = [];
  /** seam: read by load-more/ (end-of-list check); reset by global-search/ (idle). */
  total = 0;

  constructor(
    readonly config: ListConfig,
    private readonly wrapper: Element,
    /** seam: global-search/ clears its rendered cards when going idle. */
    readonly results: Element,
  ) {
    this.state = {
      q: "",
      filters: {},
      matchByField: {},
      sort: config.sort,
      visibleCount: config.initialLimit,
    };
    this.source =
      config.mode === "search" ? new SearchDataSource(config) : new ServerDataSource(config);
    // A search block starts from an empty container — there are no
    // server-rendered items for a failure to fall back to.
    this.ssrIntact = config.mode === "list";
  }

  /** Message prefix: `list "blog"` / `search "site"`. seam: used by feature warnings. */
  get label(): string {
    return `${this.config.mode} "${this.config.key}"`;
  }

  // --- element lookups, scoped to this list --------------------------------

  /** seam: used by the feature binders to find their controls. */
  scoped(selector: string): Element[] {
    return Array.from(document.querySelectorAll(selector)).filter((el) => this.owns(el));
  }

  /** A control belongs to this list via `data-slater-for`, or by containment. */
  private owns(el: Element): boolean {
    const forKey = el.getAttribute("data-slater-for");
    if (forKey) return forKey === this.config.key;
    const nearest = el.closest("[data-slater-list], [data-slater-search-list]");
    if (nearest) return nearest === this.wrapper;
    return (
      document.querySelectorAll("[data-slater-list], [data-slater-search-list]").length === 1
    );
  }

  /** seam: used by global-search/ (idle chrome). */
  get emptyEl(): Element | null {
    return document.querySelector(attrSelector("data-slater-empty", this.config.key));
  }
  /** seam: used by load-more/ and global-search/. */
  get loadMoreEl(): Element | null {
    return document.querySelector(attrSelector("data-slater-load-more", this.config.key));
  }
  /** seam: used by global-search/ (idle chrome). */
  get countEl(): Element | null {
    return document.querySelector(attrSelector("data-slater-count", this.config.key));
  }
  /** Search mode's idle/"start typing" element, shown while the query is empty. */
  get initialEl(): Element | null {
    return document.querySelector(attrSelector("data-slater-initial", this.config.key));
  }

  // --- lifecycle -----------------------------------------------------------

  async init(): Promise<void> {
    if (this.config.mode === "search") {
      // Search templates are authored scaffolding in a static div, so the
      // picker removes them from the DOM as it collects them.
      this.template = findSearchTemplates(this.results);
      if (!this.template) {
        warn(`${this.label}: no [data-slater-template] inside the results container.`);
        return;
      }
    } else {
      // A group merges collections, so authored [data-slater-template] cards
      // build a per-collection picker. Originals stay put: the first successful
      // render replaceChildren()s them away, and until then they are the SSR/
      // failure fallback — same lifecycle as a single template card.
      const picker = this.config.group ? buildTemplatePicker(this.results, false) : null;
      if (picker) {
        this.template = picker;
      } else {
        const found = findTemplate(this.results);
        if (!found) {
          warn(`${this.label}: no template found inside the results container.`);
          return;
        }
        // Detach the template before the first render so it is never itself shown.
        this.template = found.cloneNode(true) as Element;
      }
    }

    this.bindControls();

    const countEl = this.countEl;
    if (countEl && !countEl.hasAttribute("aria-live")) {
      countEl.setAttribute("aria-live", "polite");
    }

    if (this.config.mode === "search") {
      await initGlobalSearch(this);
      return;
    }

    await this.load("replace");
  }

  private bindControls(): void {
    // The same [data-slater-search] input serves both modes; the wrapper's
    // mode picks the binder (mode is immutable after construction).
    if (this.config.mode === "search") bindGlobalSearchInput(this);
    else bindListSearchInput(this);

    bindFilterControls(this);

    bindLoadMore(this);

    for (const clear of this.scoped("[data-slater-clear]")) {
      clear.addEventListener("click", (event) => {
        event.preventDefault();
        this.clearAll();
      });
    }
  }

  private clearAll(): void {
    for (const controls of this.filterControls.values()) {
      for (const control of controls) {
        if (control instanceof HTMLSelectElement) control.selectedIndex = 0;
        if (control instanceof HTMLInputElement) control.checked = false;
      }
    }
    for (const input of this.scoped("[data-slater-search]")) {
      (input as HTMLInputElement).value = "";
    }
    this.state.q = "";
    this.state.filters = {};
    this.resetAndLoad();
  }

  /** seam: called by the feature binders after any state change. */
  resetAndLoad(): void {
    this.state.visibleCount = this.config.initialLimit;
    // An empty search query is not a valid server request — go idle instead.
    // This also covers clearAll(), which blanks q before calling here.
    if (this.config.mode === "search" && !this.state.q) {
      showIdle(this);
      return;
    }
    void this.load("replace");
  }

  // --- data ----------------------------------------------------------------

  /** Resolves true when the render succeeded. seam: called by the feature binders. */
  async load(
    mode: "replace" | "append",
    offset = 0,
    limit = this.state.visibleCount,
  ): Promise<boolean> {
    this.wrapper.setAttribute("data-slater-loading", "true");
    try {
      const response = await this.source.query(this.state, offset, limit);
      const ctx = this.hookCtx(mode);
      // Hooks get a copy — the datasource caches the response object by
      // reference, so filtering the cached array would corrupt later hits.
      const items = runTransformItems([...response.items], ctx);
      this.total = response.total;

      // The server clamps `limit` to its own ceiling and echoes what it granted.
      // Page off that, not off what we asked for: a `data-slater-page-size` above
      // the cap would otherwise leave visibleCount at the request, and the next
      // load-more would start past the gap and skip every item in between.
      this.state.visibleCount = offset + (response.limit ?? limit);
      this.rendered = mode === "append" ? [...this.rendered, ...items] : items;
      this.ssrIntact = false;

      renderItems(this.results, this.template!, items, this.config, mode, ctx);
      applyFacets(this, response.facets);
      this.updateChrome(response.total);
      runRender({ ...ctx, total: this.total, rendered: this.rendered.length });

      for (const message of response.warnings ?? []) warn(`${this.label}: ${message}`);
      return true;
    } catch (error) {
      // A superseded request is not a failure — a newer one is already running.
      if (error instanceof DOMException && error.name === "AbortError") return false;
      this.handleFailure(error);
      return false;
    } finally {
      this.wrapper.removeAttribute("data-slater-loading");
    }
  }

  /**
   * A broken embed must degrade to a working Webflow page.
   *
   * Before the first successful response the server-rendered items are still in
   * the DOM, so the correct action is to touch nothing: an unsynced collection,
   * a 429, or an offline visitor leaves the page exactly as Webflow published it.
   * After that, the last good render stays on screen.
   */
  private handleFailure(error: unknown): void {
    const status = error instanceof HttpError ? error.status : 0;
    const detail = error instanceof Error ? error.message : String(error);

    if (this.ssrIntact) {
      warn(`${this.label}: ${detail}. Leaving Webflow's rendered items in place.`);
      return;
    }
    if (status === 429) {
      warn(`${this.label}: rate limited. Keeping the current results.`);
      return;
    }
    // Payment-gated: the site's Slater plan lapsed or doesn't include this
    // feature. Degrades like any other failure; the message says how to fix it.
    if (status === 402) {
      warn(`${this.label}: ${detail}. Upgrade the site's Slater plan to restore this feature.`);
      return;
    }
    // A search block has no SSR fallback: before its first successful response
    // the container is simply empty, and empty it stays.
    if (this.config.mode === "search" && this.rendered.length === 0) {
      warn(`${this.label}: ${detail}. Nothing rendered; the page is left as-is.`);
      return;
    }
    warn(`${this.label}: ${detail}. Keeping the last successful render.`);
  }

  // --- chrome --------------------------------------------------------------

  private updateChrome(total: number): void {
    // In search mode the container can hold non-card children (input, states),
    // so childElementCount would overcount and hide load-more early.
    const shown =
      this.config.mode === "search" ? this.rendered.length : this.results.childElementCount;

    const emptyEl = this.emptyEl;
    if (emptyEl) (emptyEl as HTMLElement).style.display = total === 0 ? "" : "none";

    // `visibleCount >= total` also ends paging: with a filtering transform
    // hook, shown can stay below the server total forever while loadMore would
    // no-op. Without hooks the two conditions agree (shown = min(visible, total)).
    const loadMoreEl = this.loadMoreEl;
    if (loadMoreEl) {
      (loadMoreEl as HTMLElement).style.display =
        shown >= total || this.state.visibleCount >= total ? "none" : "";
    }

    const countEl = this.countEl;
    if (countEl) countEl.textContent = String(total);

    // A real response — even zero results — means the block is no longer idle.
    const initialEl = this.initialEl;
    if (initialEl) (initialEl as HTMLElement).style.display = "none";

    debug(`${this.label}: showing ${shown} of ${total}`);
  }

  /**
   * Re-render everything currently on screen, e.g. after a back/forward-cache
   * restore. Replays all loaded pages, not just the most recent one.
   * `this.rendered` already holds transformed items, so transformItems is NOT
   * re-run (a second pass could double-apply a mapping hook); the render-time
   * hooks fire again so per-card DOM annotations are restored.
   */
  rehydrate(): void {
    if (this.rendered.length === 0 || !this.template) return;
    const ctx = this.hookCtx("replace");
    renderItems(this.results, this.template, this.rendered, this.config, "replace", ctx);
    this.updateChrome(this.total);
    runRender({ ...ctx, total: this.total, rendered: this.rendered.length });
  }

  private hookCtx(mode: "replace" | "append"): HookCtx {
    return { key: this.config.key, mode, query: this.state.q, config: this.config };
  }
}
